"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type SquashCourtPreviewProps = {
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
};

// ─── Court geometry (one-point perspective through the glass back wall) ─────
// Vanishing point (150, 40). The front wall spans x 84–216 with the floor at
// y 100, 6.4m wide → 20.6 units/m. Depth d (m from the front wall) scales by
// S = 14.39 / (14.39 − d). Front wall: out line 4.57m, service line 1.78m,
// tin 0.48m; the side-wall out line falls to 2.13m at the back. Floor: short
// line 5.44m back, 1.6m service boxes behind it, half-court line to the back.

const FRONT_L = 84;
const FRONT_R = 216;
const FLOOR_Y = 100;
const PX_PER_M = 20.6;
const OUT_Y = FLOOR_Y - 4.57 * PX_PER_M;
const SERVICE_Y = FLOOR_Y - 1.78 * PX_PER_M;
const TIN_Y = FLOOR_Y - 0.48 * PX_PER_M;
/** Where the floor/side-wall seams leave the frame at x = 0 / 300. */
const SEAM_EXIT_Y = 40 + 60 * (150 / 66);
/** Side-wall out line: from the front corner down toward the back. */
const SIDE_OUT_EXIT_Y = OUT_Y + 84 * 0.6065;

const SHORT_Y = 136.5;
const SHORT_HALF = 66 * 1.608;
const BOX_Y = 157.5;
const BOX_HALF = 66 * 1.958;

const FLOOR = `M${FRONT_L},${FLOOR_Y}L${FRONT_R},${FLOOR_Y}L300,${SEAM_EXIT_Y}L300,220L0,220L0,${SEAM_EXIT_Y}Z`;
const LEFT_WALL = `M0,0L${FRONT_L},0L${FRONT_L},${FLOOR_Y}L0,${SEAM_EXIT_Y}Z`;
const RIGHT_WALL = `M${FRONT_R},0L300,0L300,${SEAM_EXIT_Y}L${FRONT_R},${FLOOR_Y}Z`;

const FLOOR_LINES = [
  `M${150 - SHORT_HALF},${SHORT_Y}L${150 + SHORT_HALF},${SHORT_Y}`,
  `M150,${SHORT_Y}L150,220`,
  `M${150 - BOX_HALF},${BOX_Y}L${150 - 32.96 * 1.958},${BOX_Y}`,
  `M${150 - 32.96 * 1.608},${SHORT_Y}L${150 - 32.96 * 1.958},${BOX_Y}`,
  `M${150 + 32.96 * 1.958},${BOX_Y}L${150 + BOX_HALF},${BOX_Y}`,
  `M${150 + 32.96 * 1.608},${SHORT_Y}L${150 + 32.96 * 1.958},${BOX_Y}`,
].join("");
const WALL_LINES = [
  `M${FRONT_L},${OUT_Y}L${FRONT_R},${OUT_Y}`,
  `M${FRONT_L},${SERVICE_Y}L${FRONT_R},${SERVICE_Y}`,
  `M${FRONT_L},${TIN_Y}L${FRONT_R},${TIN_Y}`,
  `M${FRONT_L},${OUT_Y}L0,${SIDE_OUT_EXIT_Y}`,
  `M${FRONT_R},${OUT_Y}L300,${SIDE_OUT_EXIT_Y}`,
].join("");
const CORNERS = [
  `M${FRONT_L},0L${FRONT_L},${FLOOR_Y}`,
  `M${FRONT_R},0L${FRONT_R},${FLOOR_Y}`,
  `M${FRONT_L},${FLOOR_Y}L0,${SEAM_EXIT_Y}`,
  `M${FRONT_R},${FLOOR_Y}L300,${SEAM_EXIT_Y}`,
].join("");

// Maple boards run front-to-back, converging on the vanishing point.
const BOARD_STEP = (FRONT_R - FRONT_L) / 20;
const boardX = (k: number) => FRONT_L + k * BOARD_STEP;
const floorX = (x: number) => 150 + (x - 150) * 3;
const BOARD_SEAMS = Array.from({ length: 31 }, (_, i) => i - 5)
  .map((k) => `M${boardX(k)},${FLOOR_Y}L${floorX(boardX(k))},220`)
  .join("");
const BOARD_TONES = Array.from({ length: 30 }, (_, i) => i - 5)
  .filter((k) => k % 3 === 0)
  .map(
    (k) =>
      `M${boardX(k)},${FLOOR_Y}L${boardX(k + 1)},${FLOOR_Y}L${floorX(boardX(k + 1))},220L${floorX(boardX(k))},220Z`,
  )
  .join("");
const BALL_MARKS = [
  [128, 68],
  [158, 56],
  [174, 74],
  [116, 50],
  [142, 82],
  [186, 60],
];

function SquashCourtArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-front`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#c9c3b8" />
            <stop offset="0.5" stopColor="#ece7de" />
            <stop offset="1" stopColor="#d9d3c8" />
          </linearGradient>
          <linearGradient id={`${id}-left`} x1="1" y1="0" x2="0" y2="0">
            <stop offset="0" stopColor="#d4cec3" />
            <stop offset="1" stopColor="#8f8a81" />
          </linearGradient>
          <linearGradient id={`${id}-right`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#d4cec3" />
            <stop offset="1" stopColor="#8f8a81" />
          </linearGradient>
          <linearGradient id={`${id}-floor`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e9b872" />
            <stop offset="0.45" stopColor="#d49a50" />
            <stop offset="1" stopColor="#a8692c" />
          </linearGradient>
        </defs>

        <path d={LEFT_WALL} fill={`url(#${id}-left)`} />
        <path d={RIGHT_WALL} fill={`url(#${id}-right)`} />
        <rect
          x={FRONT_L}
          width={FRONT_R - FRONT_L}
          height={FLOOR_Y}
          fill={`url(#${id}-front)`}
        />
        {/* The tin: a hollow board under the bottom line. */}
        <rect
          x={FRONT_L}
          y={TIN_Y}
          width={FRONT_R - FRONT_L}
          height={FLOOR_Y - TIN_Y}
          fill="#b8b1a5"
        />
        {BALL_MARKS.map(([x, y]) => (
          <ellipse
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            rx="1.8"
            ry="1.2"
            fill="rgba(20,20,20,0.16)"
          />
        ))}

        <path d={FLOOR} fill={`url(#${id}-floor)`} />
        <path d={BOARD_TONES} fill="rgba(120,60,10,0.09)" />
        <path
          d={BOARD_SEAMS}
          {...PAINT_LINE}
          stroke="rgba(90,45,10,0.22)"
          strokeWidth="0.6"
        />

        <path
          d={CORNERS}
          {...PAINT_LINE}
          stroke="rgba(60,50,40,0.28)"
          strokeWidth="1"
        />
        <path d={WALL_LINES} {...PAINT_LINE} stroke="#d42a2a" strokeWidth="2" />
        <path
          d={FLOOR_LINES}
          {...PAINT_LINE}
          stroke="#c8262b"
          strokeWidth="1.8"
        />
      </CourtSvg>

      {/* Lighting: overhead panels wash the front wall, their reflection
          pools on the lacquered maple, and the glass back wall we look
          through catches two soft streaks. */}
      <div className="absolute inset-x-0 top-0 h-[60px] bg-[radial-gradient(ellipse_40%_90%_at_50%_0%,rgba(255,252,240,0.4),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_30%_26%_at_50%_58%,rgba(255,240,205,0.4),transparent_72%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,rgba(40,18,4,0.42)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_18%,rgba(255,255,255,0.07)_22%,transparent_27%,transparent_70%,rgba(255,255,255,0.05)_74%,transparent_79%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.4)_100%)]" />
    </>
  );
}

const SquashBall = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "absolute overflow-hidden rounded-full bg-[radial-gradient(circle_at_35%_30%,#55524e_0%,#1d1b19_50%,#050505_100%)] shadow-[0_0_0_1px_rgba(255,255,255,0.12)]",
      className,
    )}
  >
    {/* Double yellow dot: the competition ball. */}
    <div className="absolute top-[38%] left-[46%] h-[22%] w-[22%] rounded-full bg-[#ffd42a]" />
    <div className="absolute top-[38%] left-[68%] h-[22%] w-[22%] rounded-full bg-[#ffd42a]" />
  </div>
);

export function SquashCourtPreview({
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  games,
}: SquashCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourTeamWon = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourGames > 0 ||
      matchScore.opponentGames > 0 ||
      (games?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Squash"
      frameClassName="bg-[#a8692c]"
      matchState={matchState}
      scene={<SquashCourtArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[88%] left-[32%] h-[5px] w-[16px]" />
            <SquashBall className="top-[84%] left-[33%] h-[10px] w-[10px]" />
          </>
        ) : (
          <>
            {/* Ball driven at the front wall, shadow on the boards. */}
            <PropShadow className="top-[53%] left-[56%] h-[5px] w-[16px]" />
            <div className="absolute top-[44%] left-[59%] h-[4px] w-[40px] rotate-[32deg] rounded-full bg-[linear-gradient(90deg,rgba(40,30,20,0.4),transparent)]" />
            <SquashBall className="top-[36%] left-[57%] h-[11px] w-[11px]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourTeamWon}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourGames}
            right={matchScore.opponentGames}
            leftWinner={yourTeamWon}
            rightWinner={opponentWon}
            breakdown={games}
            accentClassName="bg-[#e3342f] shadow-[0_0_12px_rgba(227,52,47,0.75)]"
          />
        ) : null
      }
    />
  );
}
